// @flow
import { useEffect, useState } from 'react';
import _ from 'lodash';
import i18n from 'i18n-js';

import api from 'utils/api-utils';
import Toastr from 'utils/toastr-util';

import type { CurrencyConfig } from './currencyConfig';

const CURRENCY_CONFIG_URL = '/v1/points-currency-config';

type Result = {
  configs: ?Array<CurrencyConfig>,
  error: boolean,
};

// Shared by every concurrently-mounting hook instance (e.g. CurrencyRateTooltip
// and CurrencyAmountPreview mounting together on the same form, or several
// CashbackForm rows mounting at once) so they collapse into a single network
// call instead of one per component. Cleared once the request settles, so a
// later, non-concurrent mount still gets its own fresh fetch.
let inFlightRequest: ?Promise<Array<CurrencyConfig>> = null;

function fetchCurrencyConfigs(appState: Object): Promise<Array<CurrencyConfig>> {
  if (!inFlightRequest) {
    inFlightRequest = api.get(appState, CURRENCY_CONFIG_URL)
      .then((response: Object) => _.get(response, 'data.currencyConfigs', []))
      .catch((err) => {
        // Surface the failure like every other API call in this app (Toastr),
        // even though callers still fail open (no amount is blocked by this).
        // Runs once per actual request, not once per hook instance, since
        // this .catch sits on the shared/deduped promise above.
        Toastr.error(i18n.t('currency_preview.load_error'));
        throw err;
      })
      .finally(() => {
        inFlightRequest = null;
      });
  }
  return inFlightRequest;
}

// Test-only escape hatch: clears the shared in-flight request so each test
// starts with a clean slate instead of inheriting another test's pending
// (e.g. deliberately never-resolving) mock.
export function resetCurrencyConfigsCacheForTests() {
  inFlightRequest = null;
}

// Fetches the Uno points currency config once on mount, deduping concurrent
// fetches from other instances of this hook via the module-level cache above.
export default function useCurrencyConfigs(appState: Object): Result {
  const [configs, setConfigs] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchCurrencyConfigs(appState)
      .then((currencyConfigs: Array<CurrencyConfig>) => {
        if (!cancelled) setConfigs(currencyConfigs);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
    // Config is loaded once on mount only; previews are computed client-side.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { configs, error };
}
